/**
 * What a hypothesis rests on, filled from the API (PRD-0075 R-E3/R-E4): its research runs with
 * *Spustiť výskum*, and its investigations with *Pridať dôkaz* and their assessment notes.
 */
import type { UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { EvidenceOption } from '@/components/analytic-hypotheses/EvidencePicker'
import {
  type EvidenceLinking,
  HypothesisEvidenceSection,
  type LinkedInvestigationsView,
} from '@/components/analytic-hypotheses/HypothesisEvidenceSection'
import {
  HypothesisResearchSection,
  type ResearchRunsView,
} from '@/components/analytic-hypotheses/HypothesisResearchSection'
import { useCaseAsOf } from '@/features/case-as-of/CaseAsOf'
import {
  useInvestigationsStore,
  useInvestigationsStoreApi,
} from '@/stores/investigations-store-provider'
import type { HypothesisResearchRunsResponse } from '@/types/analytic-hypothesis'
import { formatDate } from '@/utils/date'
import { useHypothesisEvidenceWrites, useHypothesisInvestigations } from './evidence-queries'
import { investigationPath, researchPath } from './paths'
import type { WriteOutcome } from './queries'

/** A write's failure as a toast; its success as ``done`` says, if anything. */
async function report<T>(outcome: Promise<WriteOutcome<T>>, done?: string): Promise<boolean> {
  const settled = await outcome
  if (settled.message) toast.error(settled.message)
  else if (done) toast.success(done)
  return settled.ok
}

export interface HypothesisPanelProps {
  caseId: string
  hypothesisId: string
  /** The reader may write (a collaborator). */
  canWrite: boolean
  /** The hypothesis is open: it takes new research and new evidence. */
  open: boolean
}

export function HypothesisResearchPanel({
  runs,
  ...props
}: HypothesisPanelProps & { runs: UseQueryResult<HypothesisResearchRunsResponse, unknown> }) {
  const { t } = useTranslation()
  const writes = useHypothesisEvidenceWrites(props.caseId, props.hypothesisId)
  const view: ResearchRunsView = runs.data
    ? { status: 'ready', runs: runs.data.runs }
    : runs.isError
      ? { status: 'failed', onRetry: () => void runs.refetch() }
      : { status: 'loading' }
  const start =
    props.canWrite && props.open
      ? {
          onStart: () =>
            void report(writes.startResearch(), t('analyticHypotheses.research.started')),
          pending: writes.starting,
        }
      : null
  return (
    <HypothesisResearchSection
      view={view}
      runHref={run => researchPath(props.caseId, run.research_id)}
      start={start}
    />
  )
}

export function HypothesisEvidencePanel({
  caseId,
  hypothesisId,
  canWrite,
  open,
}: HypothesisPanelProps) {
  const { i18n } = useTranslation()
  const { asOf } = useCaseAsOf()
  const query = useHypothesisInvestigations(caseId, hypothesisId, asOf)
  const writes = useHypothesisEvidenceWrites(caseId, hypothesisId)
  const storeApi = useInvestigationsStoreApi()
  const saved = useInvestigationsStore(state => state.saved)
  const savedLoading = useInvestigationsStore(state => state.isLoading)
  const linked = query.data?.investigations

  const candidates = useMemo<EvidenceOption[] | null>(() => {
    if (savedLoading || !linked) return null
    const here = new Set(linked.map(investigation => investigation.id))
    return saved
      .filter(investigation => !here.has(investigation.id))
      .map(investigation => ({
        id: investigation.id,
        label: investigation.title,
        hint: formatDate(investigation.updatedAt, i18n.language),
      }))
  }, [saved, savedLoading, linked, i18n.language])

  // The investigation's own page reads its link from the investigations store: bring it along.
  const relink = async (outcome: Promise<WriteOutcome<unknown>>, investigationId: string) => {
    if (await report(outcome)) {
      await storeApi
        .getState()
        .fetchOne(investigationId)
        .catch(() => undefined)
    }
  }
  const linking: EvidenceLinking | null = canWrite
    ? {
        candidates,
        onLink: open ? id => void relink(writes.link(id), id) : null,
        onUnlink: investigation => void relink(writes.unlink(investigation.id), investigation.id),
        pending: writes.linking,
      }
    : null
  const view: LinkedInvestigationsView = linked
    ? { status: 'ready', investigations: linked }
    : query.isError
      ? { status: 'failed', onRetry: () => void query.refetch() }
      : { status: 'loading' }

  return (
    <HypothesisEvidenceSection
      view={view}
      investigationHref={id => investigationPath(caseId, id)}
      linking={linking}
    />
  )
}
